import { RefObject, useEffect, useRef, useState } from 'react';
import { mountWalleyIframe, WalleyIframeHandle } from '../services/walleyIframe';
import { WalleyIframePayload } from '../services/api/walley/WalleyApi';

// React lifecycle wrapper around `mountWalleyIframe`.
//
// Usage:
//   const { outerRef, innerRef } = useWalleyIframe(payload, 'walley-checkout');
//   return (
//     <div ref={outerRef} className="walley-outer">
//       <div ref={innerRef} id="walley-checkout" className="walley-inner" />
//     </div>
//   );
//
// - Mounts the Walley loader once `payload` is non-null and the refs are
//   attached. Re-mounts whenever `loaderScript` or `publicToken` change
//   (e.g. retry with a fresh checkout session).
// - On unmount, destroys the loader script and tears down all observers.
// - When `payload` is null/undefined, no iframe is mounted; this is the
//   right state during the loading or error UI before the BE response.

export interface UseWalleyIframeReturn {
  outerRef: RefObject<HTMLDivElement | null>;
  innerRef: RefObject<HTMLDivElement | null>;
  /** True once the Walley loader has injected its iframe into `innerRef`.
   *  Use this to swap a placeholder/spinner for the iframe in render. */
  iframeReady: boolean;
}

export default function useWalleyIframe(
  payload: WalleyIframePayload | null | undefined,
  containerId: string,
  onError?: (err: Event | string) => void,
  lang?: string,
): UseWalleyIframeReturn {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [iframeReady, setIframeReady] = useState(false);

  // Latest onError without re-mounting when the callback identity changes.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  useEffect(() => {
    if (!payload || !outerRef.current || !innerRef.current) return;

    setIframeReady(false);  // each fresh payload starts the placeholder cycle over
    const handle: WalleyIframeHandle = mountWalleyIframe({
      loaderScript: payload.loaderScript,
      publicToken: payload.publicToken,
      containerId,
      outer: outerRef.current,
      inner: innerRef.current,
      onError: (err) => onErrorRef.current?.(err),
      onIframeReady: () => setIframeReady(true),
      lang,
    });

    return () => {
      handle.destroy();
      setIframeReady(false);
    };
    // Re-mount only when the meaningful payload contents, target container,
    // or display language change — Walley reads `data-lang` once at script
    // init, so a mid-flow language toggle must remount the loader.
  }, [payload?.loaderScript, payload?.publicToken, containerId, lang]);

  return { outerRef, innerRef, iframeReady };
}
